<template>
  <section class="rqa_compose">
    <button type="button" class="rqa_back" @click="back">
      <span aria-hidden="true">→</span> חזרה לפניות
    </button>
    <div class="rqa_chips">
      <span v-for="key in chipKeys" :key="key" class="rqa_chip">
        <span>{{ chipText(key) }}</span>
        <button type="button" class="rqa_chip_remove" :aria-label="'ביטול תגית ' + chipText(key)" @click="removeChip(key)">×</button>
      </span>
      <span v-if="!chipKeys.length" class="rqa_muted rqa_small">בלי קישור לעמוד</span>
      <a href="#" class="rqa_link" @click.prevent="useCurrentPage">השתמשי בעמוד הנוכחי</a>
    </div>
    <textarea
      ref="input"
      :value="draft"
      class="rqa_textarea"
      rows="4"
      :maxlength="maxChars"
      placeholder="כתבי במה אפשר לעזור…"
      aria-label="פנייה חדשה"
      @input="onInput"
      @keydown.ctrl.enter.prevent="send"
      @keydown.meta.enter.prevent="send"></textarea>
    <div class="rqa_screenshot">
      <p class="rqa_screenshot_label">צילום מסך (לא חובה)</p>
      <image-upload-area :current-image="screenshot" :disabled="sending" :labels="screenshotLabels" :show-icons="false" @image-select="onScreenshot" />
    </div>
    <div class="rqa_compose_footer">
      <span class="rqa_counter" :class="{ rqa_counter_over: draft.length > maxChars }">{{ draft.length }} / {{ maxChars }}</span>
      <button type="button" class="button is-info rqa_send" :class="{ 'is-loading': sending }" :disabled="!canSend" @click="send">שליחה</button>
    </div>
    <p v-if="error" class="rqa_error" role="alert">{{ error }}</p>
  </section>
</template>
<script>
import { mapState } from 'vuex'
import {
  buildContextFromRoute,
  contextIdKeys,
  removeContextKey,
  chipLabel,
  contextPayload
} from '@/utils/recruiterQaContext'
import { getOwner } from '@/custom/classes/my_utils.js'
import { uuidv4 } from '@/store/modules/recruiterQa'
import { screenshotPayload } from '@/services/recruiterQaApi'
import ImageUploadArea from '@/components/ImageUploadArea.vue'

const MAX_CHARS = 4000
const SCREENSHOT_LABELS = {
  title: 'אפשר להדביק צילום מסך (Ctrl+V) או לגרור קובץ לכאן',
  pick: 'או ללחוץ כאן ולבחור קובץ מהמחשב',
  limits: 'קובץ תמונה אחד, עד 5MB',
  replace: 'החלפה',
  remove: 'מחיקה',
  drop: 'אפשר לשחרר כאן את התמונה'
}
const NEW_DRAFT_KEY = 'new'

export default {
  name: 'RecruiterQaCompose',
  components: { ImageUploadArea },
  data () {
    return {
      screenshot: null,
      screenshotLabels: SCREENSHOT_LABELS,
      names: {},
      sending: false,
      error: '',
      requestUuid: null,
      maxChars: MAX_CHARS
    }
  },
  computed: {
    ...mapState('recruiterQa', ['drafts', 'newContext']),
    draft () { return this.drafts[NEW_DRAFT_KEY] || '' },
    chipKeys () { return contextIdKeys(this.newContext) },
    canSend () {
      const len = this.draft.trim().length
      return !this.sending && len > 0 && this.draft.length <= MAX_CHARS
    }
  },
  watch: {
    newContext () { this.loadNames() }
  },
  mounted () {
    this.loadNames()
    this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
  },
  methods: {
    back () { this.$store.dispatch('recruiterQa/backToList') },
    onInput (event) {
      // Typing freezes the chips (the drawer stops following the route).
      this.$store.commit('recruiterQa/SET_DRAFT', { key: NEW_DRAFT_KEY, text: event.target.value })
      this.requestUuid = null
      this.error = ''
    },
    // A different image is a different payload: never replay it under the old request key.
    onScreenshot (file) {
      this.screenshot = file || null
      this.requestUuid = null
      this.error = ''
    },
    setContext (ctx) {
      this.$store.commit('recruiterQa/SET_NEW_CONTEXT', ctx)
      this.requestUuid = null
      this.error = ''
    },
    useCurrentPage () { this.setContext(buildContextFromRoute(this.$route)) },
    removeChip (key) { this.setContext(removeContextKey(this.newContext, key)) },
    chipText (key) { return chipLabel(key, this.newContext, this.names) },
    async loadNames () {
      this.names = {}
      const requested = this.newContext
      if (!contextIdKeys(requested).length) return
      const res = await this.$store.dispatch('recruiterQa/fetchContextNames', contextPayload(requested))
      if (this.newContext !== requested) return // superseded
      if (res.ok) {
        this.names = res.names || {}
      } else if (res.error && res.error.kind === 'context_invalid') {
        this.error = res.message
      }
    },
    async send () {
      if (!this.canSend) return
      this.sending = true
      this.error = ''
      if (!this.requestUuid) this.requestUuid = uuidv4()
      let screenshot = null
      try {
        screenshot = await screenshotPayload(this.screenshot)
      } catch (e) {
        this.sending = false
        this.error = 'לא הצלחנו לקרוא את צילום המסך — נסי לצרף אותו שוב'
        return
      }
      const res = await this.$store.dispatch('recruiterQa/submit', {
        question: this.draft.trim(),
        parentId: null,
        context: contextPayload(this.newContext, getOwner().full_name),
        requestUuid: this.requestUuid,
        screenshot
      })
      this.sending = false
      if (res.ok) {
        this.requestUuid = null
        this.screenshot = null
        this.$store.commit('recruiterQa/SET_DRAFT', { key: NEW_DRAFT_KEY, text: '' })
        this.$store.commit('recruiterQa/SET_NEW_CONTEXT', buildContextFromRoute(this.$route))
        if (res.rootId) this.$store.dispatch('recruiterQa/openTicket', { rootId: res.rootId })
      } else {
        // Keep the same key so a resend of the same text is idempotent.
        this.requestUuid = res.requestUuid || null
        this.error = res.message
      }
    }
  }
}
</script>
<style scoped>
.rqa_compose {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}
.rqa_back {
  background: none;
  border: none;
  color: #3273dc;
  cursor: pointer;
  font-size: 14px;
  padding: 0 0 8px;
  min-height: 36px;
}
.rqa_chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 8px;
}
.rqa_chip {
  display: inline-flex;
  align-items: center;
  background-color: #eef3fb;
  color: #204d74;
  border-radius: 14px;
  padding: 2px 10px;
  font-size: 12px;
  margin-left: 6px;
  margin-bottom: 4px;
}
.rqa_chip_remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #204d74;
  font-size: 14px;
  margin-right: 4px;
  padding: 0;
}
.rqa_link {
  color: #3273dc;
  font-size: 12px;
  cursor: pointer;
  margin-right: 4px;
}
.rqa_link:hover {
  color: #3273dc;
  text-decoration: underline;
}
.rqa_textarea {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 160px;
  max-height: 360px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 10px 12px !important;
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  direction: rtl;
  resize: vertical;
}
.rqa_screenshot {
  margin-top: 8px;
}
.rqa_screenshot_label {
  font-size: 12px;
  color: #555;
  margin: 0 0 4px;
}
.rqa_compose_footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.rqa_counter {
  font-size: 11px;
  color: #888;
}
.rqa_counter_over {
  color: #c9302c;
}
.rqa_muted {
  color: #888;
}
.rqa_small {
  font-size: 12px;
}
.rqa_error {
  color: #c9302c;
  font-size: 12px;
  margin: 6px 0 0;
}
@media (max-width: 767px) {
  .rqa_send { min-height: 44px; min-width: 96px; }
  .rqa_chip_remove { min-width: 32px; min-height: 32px; }
}
</style>
